import type { MetaFunction } from "@remix-run/node";
import { useNavigate, useSearchParams, Outlet } from "@remix-run/react";
import { Autocomplete } from '../components/autocomplete';
import { useState, useEffect, startTransition } from 'react';
import { toast, Slide } from 'react-toastify';
import { TrashIcon } from '@heroicons/react/24/outline';
import {HistoryType, HistoryEntry, clearHistory, getHistory, addToHistory} from "../client/local-history.client";

// see old code for repo name type ahead:
// remix-app/archived/search.tsx

// TODO: algolia autocomplete dark mode support
// https://discourse.algolia.com/t/dynamic-dark-mode-support-a-la-media-prefers-color-scheme-dark/15004/8

// could try to compose these using `.source`, but it gets a little complicated with start and end anchors
const GITHUB_REPO_NAME_REGEX = /^([A-Za-z0-9-]{1,50})\/([A-Za-z0-9-_\.]{1,100})$/;
const GITHUB_URL_REGEX = /^(?:http|https):\/\/github\.com\/([A-Za-z0-9-]{1,50})\/([A-Za-z0-9-_\.]{1,100})(?:\/.*)?$/;

interface RepoNameMatch {
  owner: string;
  repoName: string;
}

function getRepoNameMatch(query: string): RepoNameMatch | null {
  // check for either direct name match or url match and extract the name
  if(query.length > 300) {
    return null;
  }

  const res = GITHUB_REPO_NAME_REGEX.exec(query) ?? GITHUB_URL_REGEX.exec(query);
  if(res) {
    return {
      owner: res[1],
      repoName: res[2]
    }
  }

  return null;
}

function historyClearedToast() {
  // https://fkhadra.github.io/react-toastify/introduction/
  toast.success('Search history cleared', {
    position: "bottom-right",
    autoClose: 3000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
    transition: Slide
  });
}

function searchTemplate({ item }: {item: HistoryEntry}) {
  if(item.type === HistoryType.Search) {
    return <div><span className="w-8 text-center inline-block">🔍</span><span>{item.text}</span></div>;
  } else if (item.type === HistoryType.RepoVisit) {
    return <div><span className="w-8 text-center inline-block">◦</span><span>{item.text}</span></div>;
    // →&nbsp;&nbsp; • ◦
  }
  
}

function itemTemplate({ item }: {item: any}) {
  return (
    <div>
      <span>→ {item.owner}/{item.repoName}</span>
    </div>
  );
}

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  // use this approach since the <Autocomplete> component is rerendered/destroyed due to react stuff
  // another possibility is to get a reference to the autocomplete instance in child and imperatively call `setQuery`. Using `useImperativeHandle` in react?
  const [acInitialState, setACInitialState] = useState<any>({});
  const [historyArray, setHistoryArray] = useState<HistoryEntry[]>([]);
  const navigate = useNavigate();

  function clearLocalStorageRecent() {
    clearHistory();
    setHistoryArray([]);
    historyClearedToast();
  }
  const currentQuery = searchParams.get('q');

  useEffect(() => {
    setHistoryArray(getHistory());
    
    if (currentQuery) {
      // Use 'startTransition' to fix this error: `This Suspense boundary received an update before it finished hydrating`
      startTransition(() => {
        setACInitialState({query: currentQuery});
      });
    } else {
      // Set autocomplete focus
      // TODO: https://react.dev/reference/react/forwardRef#exposing-a-dom-node-to-the-parent-component
      // "Exposing a DOM node to the parent component"
      // https://dev.to/carlosrafael22/using-refs-in-react-functional-components-part-1-useref-callback-ref-2j5i

      // Fix due to tailwind menu item keeping focus on click
      // check `document.activeElement`
      // Set: `autoFocus={false}` in component below
      const autoCompleteInput = document.querySelector<HTMLInputElement>('input.aa-Input');
      if(autoCompleteInput) {
        autoCompleteInput.focus();
        autoCompleteInput.click(); // trying to get ios keyoard to show up
      }  
    }
  }, [currentQuery]);

  function setSearchQueryParam(query: string) {
    query = query.trim();
    setACInitialState({query});
    setHistoryArray(addToHistory(HistoryType.Search, query));
    // navigation will be triggered and loader will run before rendering new results
    navigate({
      pathname: "/search/results",
      search: `?q=${encodeURIComponent(query)}`
    });
  }

  function onSelectSearchItem(autoCompleteState: any) {
    const item: HistoryEntry = autoCompleteState.item;
    if( item.type === HistoryType.Search) {
      setSearchQueryParam(item.text);
    } else if (item.type === HistoryType.RepoVisit) {
      navigate(`/gh/${item.text}`);
    }
  }

  function onSubmit(autocompleteState) {
    // https://www.algolia.com/doc/ui-libraries/autocomplete/core-concepts/state/#state-object
    const query = autocompleteState.state.query;
    const nameMatchObj = getRepoNameMatch(query);

    if(nameMatchObj) {
      toast.success(`Navigating to: ${nameMatchObj.owner}/${nameMatchObj.repoName}`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Slide
      });

      navigate(`/gh/${nameMatchObj.owner}/${nameMatchObj.repoName}`);
      return;
    }

    setSearchQueryParam(query);
  }

  function onSelectAutoComplete(autoCompleteState: any) {
    navigate(autoCompleteState.itemUrl); // to handle mouse click on item result
  };

  function getSources({query}: {query: string}) {
    query = query.trim();

    const sources: any[] = [];
    const nameMatchObj = getRepoNameMatch(query);

    if(nameMatchObj) {
      sources.push({
        sourceId: 'matchingRepoNameSource',
        getItems() {
          return [nameMatchObj];
        },
        getItemUrl({ item }: {item: any}) {
          return `/gh/${item.owner}/${item.repoName}`;
        },
        onSelect: onSelectAutoComplete,
        templates: {
          header() {
            return <div><strong>Matching repository name:</strong></div>;
          },
          item: itemTemplate,
          noResults() {
            return 'No matching repositories found.';
          },
        },
      })

    }

    sources.push({
      sourceId: 'recentSearchesSource',
      onSelect: onSelectSearchItem,
      getItems: ({ query }: {query: string}): HistoryEntry[] => {
        return query ? historyArray.filter(historyEntry => historyEntry.text.toLowerCase().includes(query)) : historyArray;
      },
      getItemInputValue({ item } : {item: HistoryEntry}) {
        return item.text;
      },
      templates: {
        header() {
          return <div><strong>Recent:</strong></div>;
        },
        item: searchTemplate,
        noResults() {
          return 'No recent searches';
        },
      }
    });

    // TODO: also trending repos for when empty?
    return sources;
  }

  return (
    <div>
      <Autocomplete
        placeholder="Search repositories"
        autoFocus={false}
        openOnFocus={true}
        getSources={getSources}
        onSubmit={onSubmit}
        // XSS injection mitigated by autocomplete component
        initialState={acInitialState}
        enterKeyHint="search"
      />

      <div className='w-full md:w-4/5 mx-auto mt-2 text-right'>
        <div onClick={clearLocalStorageRecent} className="cursor-pointer inline-flex items-center gap-x-1 text-rr-light">
          <TrashIcon className="h-4 w-4 inline-block" />
          <span className="text-sm">Clear search history</span>
        </div>
      </div>

      <div className='mt-2'>
        <Outlet />
      </div>
    </div>
  );
}
